import React from "react";
import {
  Bank,
  Plus,
  PencilSimple,
  Trash,
  Phone,
  EnvelopeSimple,
  UserCircle,
} from "@phosphor-icons/react";

/**
 * Overview tab side column on the Vendor Detail page: Bank and PoCs.
 */
export default function VendorSidePanel({
  vendor,
  onAddPoc,
  onEditPoc,
  onDeletePoc,
}) {
  const bank = vendor.bank_account || {};
  const pocs = vendor.pocs || [];
  return (
    <div className="space-y-6">
      {/* Bank */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          <Bank size={12} /> Bank Account
        </div>
        {bank.bank_name || bank.account_number || bank.ifsc ? (
          <div className="mt-3 space-y-2 text-sm">
            {bank.bank_name && (
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Bank</span>
                <span className="text-slate-900 font-medium">{bank.bank_name}</span>
              </div>
            )}
            {bank.account_holder && (
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">Holder</span>
                <span className="text-slate-900">{bank.account_holder}</span>
              </div>
            )}
            {bank.account_number && (
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">A/C No.</span>
                <span className="text-slate-900 font-mono text-xs">
                  {bank.account_number}
                </span>
              </div>
            )}
            {bank.ifsc && (
              <div className="flex justify-between gap-2">
                <span className="text-slate-500">IFSC</span>
                <span className="text-slate-900 font-mono text-xs">{bank.ifsc}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-3 text-sm text-slate-400 italic">No bank details</div>
        )}
      </div>

      {/* PoCs */}
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="vendor-pocs-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Vendor PoCs
          </div>
          <button
            onClick={onAddPoc}
            className="inline-flex items-center gap-1 px-2 h-7 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
            data-testid="add-vendor-poc-button"
          >
            <Plus size={12} weight="bold" /> Add
          </button>
        </div>
        {pocs.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-3 text-center border border-dashed border-slate-200 rounded-md">
            No PoCs yet
          </div>
        ) : (
          <div className="space-y-2">
            {pocs.map((p) => (
              <div
                key={p.id}
                className="rounded-md border border-slate-200 bg-slate-50 p-3"
                data-testid={`vendor-poc-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <UserCircle
                      size={18}
                      weight="duotone"
                      className="text-blue-600 mt-0.5 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 text-sm">{p.name}</div>
                      {p.mobile && (
                        <a
                          href={`tel:${p.mobile}`}
                          className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600"
                        >
                          <Phone size={11} /> {p.mobile}
                        </a>
                      )}
                      {p.email && (
                        <a
                          href={`mailto:${p.email}`}
                          className="mt-0.5 ml-3 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 break-all"
                        >
                          <EnvelopeSimple size={11} /> {p.email}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      onClick={() => onEditPoc(p)}
                      className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100"
                      data-testid={`edit-vendor-poc-${p.id}`}
                    >
                      <PencilSimple size={13} />
                    </button>
                    <button
                      onClick={() => onDeletePoc(p.id)}
                      className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-vendor-poc-${p.id}`}
                    >
                      <Trash size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
